import React, {useEffect, useState} from "react";
import {
	Avatar,
	Box,
	Button,
	Card,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Link,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
	Evaluation,
	EvaluationList,
	EvaluationPoint,
	EvaluationPointRestControllerApiFactory,
	EvaluationRestControllerApiFactory,
	EvaluationResult,
	EvaluationResultRestControllerApiFactory,
	Person,
	PersonRestControllerApiFactory,
	User,
} from "arteco-api-client-ts";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import dayjs from "dayjs";
import AdminInner from "../../../../components/layout/admin_inner";
import {useApiManager} from "../../../../components/utils/api";
import {useAuthManager} from "../../../../components/utils/auth";
import {parsePathId} from "../../../../components/form/form_utils";
import {dateToString} from "../../../../components/utils/dates";
import {btnSx, fullName, initials, panelSx} from "../interview/ui";
import {PersonPicker} from "../interview/form";
import {Answers, EvaluationComments, EvaluationEditor, formatScore, ScoreBadge, ScoreBar, scoreColor} from "./ui";

const today = () => dayjs().format("YYYY-MM-DD");

/** La persona, su media y sus autoevaluaciones anteriores con la puntuación de cada una. */
const PersonHistory = ({personId, currentId}: { personId?: number, currentId?: number }) => {
	const apiManager = useApiManager();
	const [person, setPerson] = useState<Person>();
	const [history, setHistory] = useState<EvaluationList[]>();

	useEffect(() => {
		setPerson(undefined);
		setHistory(undefined);
		if (!personId) {
			return;
		}
		apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(personId))
			.then(s => setPerson(s.person)).catch(() => undefined);
		apiManager.execute(apiManager.factory(EvaluationRestControllerApiFactory).listEvaluation(personId))
			.then(list => setHistory(list.filter(e => !e.dismissed)
				.sort((a, b) => (b.evaluationDate || "").localeCompare(a.evaluationDate || ""))))
			.catch(() => setHistory([]));
	}, [personId]);

	if (!personId) {
		return <Card elevation={0} sx={panelSx}>
			<Typography variant="body2" color="text.secondary">Elige la persona para ver sus autoevaluaciones anteriores.</Typography>
		</Card>;
	}
	const others = (history || []).filter(e => e.id !== currentId);
	const rated = (history || []).filter(e => e.rating !== undefined && e.rating !== null);
	const avg = rated.length ? rated.reduce((a, e) => a + (e.rating as number), 0) / rated.length : undefined;
	// Las últimas doce, de la más antigua a la más reciente, para ver la tendencia de un vistazo.
	const trend = rated.slice(0, 12).reverse();

	return (
		<Stack spacing={3}>
			<Card elevation={0} sx={panelSx}>
				{!person ? <Skeleton variant="rounded" height={64}/> : (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Avatar sx={{width: 48, height: 48, fontWeight: 700, bgcolor: "primary.main"}}>{initials(fullName(person))}</Avatar>
						<Box sx={{minWidth: 0}}>
							<Link component={RouterLink} to={"/app/admin/employee/person/" + personId} underline="hover"
								  color="text.primary" fontWeight={700} noWrap sx={{display: "block"}}>
								{fullName(person)}
							</Link>
							<Typography variant="caption" color="text.secondary">Alta {dateToString(person.hireDate)}</Typography>
						</Box>
					</Stack>
				)}
				{history && rated.length > 0 && (
					<Box sx={{mt: 2.5}}>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Typography variant="body2" color="text.secondary">Media de {rated.length}</Typography>
							<ScoreBadge rating={avg}/>
						</Stack>
						<Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{height: 48, mt: 1.5}} aria-label="Tendencia">
							{trend.map(e => (
								<Box key={e.id} title={`${dateToString(e.evaluationDate)}: ${formatScore(e.rating)}`} sx={{
									flex: 1, borderRadius: 1, bgcolor: scoreColor(e.rating), opacity: e.id === currentId ? 1 : 0.65,
									height: `${Math.max(8, (e.rating as number) * 100 / 40)}%`,
								}}/>
							))}
						</Stack>
					</Box>
				)}
			</Card>

			<Card elevation={0} sx={panelSx}>
				<Typography variant="subtitle1" fontWeight={700}>Anteriores</Typography>
				{!history ? <Skeleton variant="rounded" height={80} sx={{mt: 2}}/> : others.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{mt: 1}}>No tiene otras.</Typography>
				) : (
					<Stack spacing={1.25} sx={{mt: 2}}>
						{others.slice(0, 8).map(e => (
							<Stack key={e.id} direction="row" spacing={1.5} alignItems="center">
								<Link component={RouterLink} underline="hover" variant="body2" fontWeight={600} sx={{width: 92}}
									  to={"/app/admin/employee/person/" + personId + "/evaluation/" + e.id}>
									{dateToString(e.evaluationDate)}
								</Link>
								<Box sx={{flexGrow: 1}}><ScoreBar rating={e.rating}/></Box>
								<Typography variant="body2" fontWeight={700} sx={{width: 28, textAlign: "right", color: scoreColor(e.rating)}}>
									{formatScore(e.rating)}
								</Typography>
							</Stack>
						))}
						{others.length > 8 && (
							<Link component={RouterLink} to={"/app/admin/employee/person/" + personId + "/evaluation"} variant="body2">
								Ver las {history.length}
							</Link>
						)}
					</Stack>
				)}
			</Card>
		</Stack>
	);
};

/**
 * Ficha de una autoevaluación para RR. HH.: las respuestas con su escala y los comentarios, que se
 * pueden corregir; al lado, la persona, su media y su tendencia. También sirve para registrar una a
 * mano (por ejemplo, la que alguien entrega en papel).
 */
export const EvaluationEditPage = () => {
	const params = useParams();
	const navigate = useNavigate();
	const apiManager = useApiManager();
	const authManager = useAuthManager();
	const ctrl = apiManager.factory(EvaluationRestControllerApiFactory);
	const pathPersonId = parsePathId(params, "personId");
	const id = parsePathId(params);

	const [bean, setBean] = useState<Evaluation>();
	const [person, setPerson] = useState<{ id?: number, label: string }>();
	const [points, setPoints] = useState<EvaluationPoint[]>();
	const [results, setResults] = useState<EvaluationResult[]>();
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);

	useEffect(() => {
		apiManager.execute(apiManager.factory(EvaluationPointRestControllerApiFactory).listEvaluationPoint())
			.then(list => setPoints([...list].sort((a, b) => a.position - b.position))).catch(() => setPoints([]));
		apiManager.execute(apiManager.factory(EvaluationResultRestControllerApiFactory).listEvaluationResult())
			.then(setResults).catch(() => setResults([]));
	}, []);

	useEffect(() => {
		if (id) {
			apiManager.execute(ctrl.getEvaluation(id)).then(e => {
				setBean(e);
				setPerson(e.person ? {id: e.person.id, label: fullName(e.person)} : undefined);
				setDirty(false);
			}).catch(() => undefined);
		} else {
			setBean({
				person: {id: pathPersonId} as Person, evaluationDate: today(), points: {},
				modifier: {id: authManager.getUserId()} as User,
			} as Evaluation);
			setDirty(false);
		}
	}, [id]);

	useEffect(() => {
		if (!id && pathPersonId) {
			apiManager.execute(apiManager.factory(PersonRestControllerApiFactory).summaryPerson(pathPersonId))
				.then(s => setPerson({id: pathPersonId, label: fullName(s.person)})).catch(() => undefined);
		}
	}, [id, pathPersonId]);

	const set = (patch: Partial<Evaluation>) => {
		setBean(b => ({...(b as Evaluation), ...patch}));
		setDirty(true);
	};

	const personId = person?.id;
	const valid = !!personId && !!bean?.evaluationDate;

	const save = () => {
		if (!bean || !valid) {
			return;
		}
		setSaving(true);
		// El backend solo necesita el id de cada respuesta.
		const pts: { [k: string]: EvaluationResult } = {};
		Object.entries(bean.points || {}).forEach(([k, v]) => v && (pts[k] = {id: v.id} as EvaluationResult));
		apiManager.execute(ctrl.saveEvaluation({...bean, person: {id: personId} as Person, points: pts}), "Autoevaluación guardada")
			.then(saved => {
				setBean(saved);
				setDirty(false);
				if (!id && saved.id) {
					navigate("/app/admin/employee/person/" + personId + "/evaluation/" + saved.id, {replace: true});
				}
			})
			.catch(() => undefined)
			.finally(() => setSaving(false));
	};

	const remove = () => {
		if (!id) {
			return;
		}
		apiManager.execute(ctrl.deleteOrDismissEvaluation(id), "Autoevaluación eliminada")
			.then(() => navigate("/app/admin/employee/evaluation"))
			.catch(() => undefined);
	};

	if (!bean || !points || !results) {
		return <AdminInner withoutPaper><Box display="flex" justifyContent="center" p={6}><CircularProgress/></Box></AdminInner>;
	}

	const answers = (bean.points || {}) as Answers;
	const rating = (() => {
		const vals = Object.values(answers).filter(Boolean).map(r => (r as EvaluationResult).level);
		return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : undefined;
	})();
	const byOwner = !!id && bean.modifier?.username && bean.person?.id && !dirty;

	return (
		<AdminInner withoutPaper>
			<Box sx={{maxWidth: 1180}}>
				<Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent="space-between" alignItems={{sm: "flex-end"}} sx={{mb: 3}}>
					<Box sx={{minWidth: 0}}>
						<Button size="small" startIcon={<ArrowBackRoundedIcon/>} onClick={() => navigate(-1)}
								sx={{...btnSx, ml: -1, mb: 0.5, color: "text.secondary"}}>
							Volver
						</Button>
						<Typography variant="h5" component="h1" fontWeight={700}>
							{person?.label ? `Autoevaluación de ${person.label}` : "Nueva autoevaluación"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{id ? (dirty ? "Cambios sin guardar" : `Del ${dateToString(bean.evaluationDate)}${byOwner ? " · enviada por " + bean.modifier.username : ""}`)
								: "Para registrar una que no se hizo desde el portal."}
						</Typography>
					</Box>
					<Stack direction="row" spacing={1} sx={{flexShrink: 0}}>
						{id && (
							<Button color="error" startIcon={<DeleteOutlineRoundedIcon/>} onClick={() => setConfirmDelete(true)} sx={btnSx}>
								Eliminar
							</Button>
						)}
						<Button variant="contained" disableElevation disabled={!valid || saving || (!!id && !dirty)} onClick={save}
								sx={{...btnSx, px: 3}}>
							{saving ? <CircularProgress size={20} color="inherit"/> : id ? "Guardar cambios" : "Guardar"}
						</Button>
					</Stack>
				</Stack>

				<Grid container spacing={3}>
					<Grid item xs={12} md={8}>
						<Stack spacing={3}>
							<Card elevation={0} sx={panelSx}>
								<Grid container spacing={2} alignItems="center">
									{!pathPersonId && !id && (
										<Grid item xs={12} sm={7}>
											<PersonPicker label="Persona" value={person} onChange={v => {
												setPerson(v);
												setDirty(true);
											}}/>
										</Grid>
									)}
									<Grid item xs={12} sm={5}>
										<TextField fullWidth type="date" label="Fecha" value={bean.evaluationDate?.substring(0, 10) || ""}
												   InputLabelProps={{shrink: true}} inputProps={{max: today()}}
												   onChange={e => set({evaluationDate: e.target.value})}/>
									</Grid>
									<Grid item xs={12} sm={pathPersonId || id ? 7 : 12}>
										<Stack direction="row" spacing={2} alignItems="center" justifyContent={{sm: pathPersonId || id ? "flex-end" : "flex-start"}}>
											<Typography variant="body2" color="text.secondary">Puntuación</Typography>
											<ScoreBadge rating={rating} size="large"/>
										</Stack>
									</Grid>
								</Grid>
							</Card>

							<EvaluationEditor points={points} results={results} answers={answers} comments={bean as EvaluationComments}
											  commentsHint="La persona no los vuelve a ver una vez enviados."
											  onAnswer={(pid, r) => set({points: {...(bean.points || {}), [String(pid)]: r}})}
											  onComment={(f, v) => set({[f]: v} as Partial<Evaluation>)}/>
						</Stack>
					</Grid>
					<Grid item xs={12} md={4}>
						<Box sx={{position: {md: "sticky"}, top: {md: 16}}}>
							<PersonHistory personId={personId} currentId={id}/>
						</Box>
					</Grid>
				</Grid>

				<Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} PaperProps={{sx: {borderRadius: 3}}}>
					<DialogTitle sx={{fontWeight: 700}}>¿Eliminar la autoevaluación?</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary">
							La de {dateToString(bean.evaluationDate)} de {person?.label}. Deja de contar para la del mes en el panel.
						</Typography>
					</DialogContent>
					<DialogActions sx={{px: 3, pb: 2.5}}>
						<Button onClick={() => setConfirmDelete(false)} sx={btnSx}>Cancelar</Button>
						<Button color="error" variant="contained" disableElevation onClick={remove} sx={btnSx}>Eliminar</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</AdminInner>
	);
};
